import { useState, useRef, useEffect } from 'preact/hooks';
import { IntegrationProvider } from '@/types/integrations';
export function useIntegrationForm({ selectedProvider, managingConnection, }) {
    const [formValues, setFormValues] = useState({ Name: '', Description: '', ApiKey: '', Channel: '' });
    const [fieldErrors, setFieldErrors] = useState({});
    const [businessErrors, setBusinessErrors] = useState([]);
    const [createError, setCreateError] = useState(null);
    const nameInputRef = useRef(null);
    // Reset form when provider changes
    useEffect(() => {
        if (selectedProvider) {
            setFormValues({ Name: selectedProvider.title, Description: '', ApiKey: '', Channel: '' });
            setCreateError(null);
            setBusinessErrors([]);
            setFieldErrors({});
        }
    }, [selectedProvider]);
    // Reset form when managing a different connection
    useEffect(() => {
        if (managingConnection) {
            setFormValues({
                Name: managingConnection.Name || '',
                Description: managingConnection.Description || '',
                ApiKey: '',
                Channel: managingConnection.Channel || ''
            });
            setCreateError(null);
            setBusinessErrors([]);
            setFieldErrors({});
        }
    }, [managingConnection]);
    const isBusinessError = (v) => !!v && typeof v === 'object' && 'Title' in v && 'Message' in v;
    const handlePanelError = (raw) => {
        setBusinessErrors([]);
        setCreateError(null);
        if (!raw)
            return;
        let parsed;
        try {
            parsed = JSON.parse(raw);
        }
        catch (syntaxError) {
            console.warn('Failed to parse error response as JSON:', syntaxError);
        }
        if (Array.isArray(parsed) && parsed.every(isBusinessError)) {
            setBusinessErrors(parsed.map(p => {
                const be = p;
                return { Title: String(be.Title), Message: String(be.Message) };
            }));
        }
        else {
            setCreateError(raw);
        }
    };
    const validate = () => {
        const errs = {};
        const nameTrim = formValues.Name.trim();
        const descTrim = formValues.Description?.trim() || '';
        const apiKeyTrim = formValues.ApiKey.trim();
        const channelTrim = formValues.Channel.trim();
        if (!nameTrim)
            errs.Name = 'Name is required.';
        if (nameTrim.length > 100)
            errs.Name = 'Name exceeds maximum length (100).';
        if (descTrim.length > 500)
            errs.Description = 'Description exceeds maximum length (500).';
        // API key is customer-supplied for third-party providers, but server-generated
        // for Webhook — skip the required check for Webhook creates.
        const isWebhookCreate = selectedProvider?.id === IntegrationProvider.Webhook;
        if (selectedProvider && !isWebhookCreate && !apiKeyTrim)
            errs.ApiKey = 'API Key is required.';
        const providerForValidation = selectedProvider
            ? selectedProvider.id
            : managingConnection?.Provider;
        const isBigMarker = providerForValidation === IntegrationProvider.BigMarker;
        if (isBigMarker) {
            if (!channelTrim)
                errs.Channel = 'Channel Id is required when Provider=BigMarker.';
            else if (channelTrim.length > 100)
                errs.Channel = 'Channel Id exceeds maximum length of 100 characters.';
        }
        else if (channelTrim.length > 100) {
            errs.Channel = 'Channel Id exceeds maximum length of 100 characters.';
        }
        setFieldErrors(errs);
        return errs;
    };
    const resetFormState = () => {
        setFormValues({ Name: '', Description: '', ApiKey: '', Channel: '' });
        setFieldErrors({});
        setBusinessErrors([]);
        setCreateError(null);
    };
    return {
        formValues,
        setFormValues,
        fieldErrors,
        setFieldErrors,
        businessErrors,
        setBusinessErrors,
        createError,
        setCreateError,
        validate,
        handlePanelError,
        isBusinessError,
        resetFormState,
        nameInputRef,
    };
}
